import { useQuery } from "@tanstack/react-query";
import { ColumnSort } from "@tanstack/react-table";

import { DEFAULT_PAGE_SIZE } from "@/domain/constants";
import { StudentFilterType } from "@/domain/student";
import { useCurrentUserId, useCurrentUserType } from "@/stores/authStore";
import type { PaginationType } from "@/utils/helpers";

import { StudentApi } from "../api/studentApi";

export const StudentQueryKeys = {
  all: ["student"] as const,
  me: () => [...StudentQueryKeys.all, "me"] as const,
  details: (id: string) => [...StudentQueryKeys.all, "details", id] as const,
  list: () => [...StudentQueryKeys.all, "list"],
  listTable: ({
    page,
    pageSize = DEFAULT_PAGE_SIZE,
    sort,
    filters,
    needsPagination,
  }: PaginationType<StudentFilterType>) =>
    [
      ...StudentQueryKeys.list(),
      page,
      pageSize,
      sort,
      filters,
      needsPagination,
    ] as const,
  count: ({ filters }: { filters?: StudentFilterType }) =>
    [...StudentQueryKeys.all, "count", filters] as const,
  options: () => [...StudentQueryKeys.list(), "options"] as const,
};

export const useCurrentStudentQuery = () => {
  const currentUserId = useCurrentUserId();
  const currentUserType = useCurrentUserType();

  const isStudentUser = currentUserType === "STUDENT";

  return useQuery({
    queryKey: StudentQueryKeys.me(),
    queryFn: () => StudentApi.getStudent(currentUserId),
    staleTime: Infinity,
    enabled: Boolean(currentUserId && isStudentUser),
  });
};

export const useGetStudentQuery = (id: string) => {
  return useQuery({
    queryKey: StudentQueryKeys.details(id),
    queryFn: () => StudentApi.getStudent(id),
    enabled: Boolean(id),
  });
};

export const useListStudentsQuery = ({
  page,
  pageSize = DEFAULT_PAGE_SIZE,
  sort,
  filters,
  needsPagination,
}: PaginationType<StudentFilterType>) => {
  return useQuery({
    queryKey: StudentQueryKeys.listTable({
      page,
      pageSize,
      sort,
      filters,
      needsPagination,
    }),
    queryFn: () =>
      StudentApi.listStudents({
        page,
        pageSize,
        sort,
        filters,
        needsPagination,
      }),
  });
};

export const useCountStudentsQuery = ({
  filters,
}: { filters?: StudentFilterType } = {}) => {
  return useQuery({
    queryKey: StudentQueryKeys.count({ filters }),
    queryFn: () => StudentApi.countStudents({ filters }),
    initialData: 0,
  });
};

export const useStudentOptionsQuery = ({
  sort = { id: "fullname", desc: false },
  filters,
  isEnabled = true,
}: {
  sort?: ColumnSort;
  filters?: StudentFilterType;
  isEnabled?: boolean;
}) => {
  return useQuery({
    queryKey: StudentQueryKeys.options(),
    queryFn: async () => {
      const students = await StudentApi.listStudentsAsOptions({
        sort,
        filters,
      });

      return students.flatMap(student =>
        student.enrolments.map(enrolment => ({
          label: `${student.first_name} ${student.last_name} - ${enrolment.course.name}`,
          value: enrolment.id,
        })),
      );
    },
    enabled: isEnabled,
  });
};
